import { useAppUser } from './admin/AppUserContext';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth, SignIn, SignUp, UserProfile, ClerkProvider } from '@clerk/clerk-react';
import { motion } from 'framer-motion';
import { AnalyticsProvider } from './components/AnalyticsProvider';
import InviteTokenCapture from './components/InviteTokenCapture';
import ImpersonationBanner from './admin/ImpersonationBanner';
import SupportSessionProvider from './admin/SupportSessionProvider';
import React from 'react';
import { RouteAnalytics } from './components/RouteAnalytics';
import PageSEO from './components/PageSEO';

// Import pages (to be created)
import LandingPage from './pages/LandingPage';
import FBLandingPage from './pages/FBLandingPage';
import StrategicPlanningSoftwarePage from './pages/StrategicPlanningSoftwarePage';
import StrategyExecutionSoftwarePage from './pages/StrategyExecutionSoftwarePage';
import BusinessStrategySoftwarePage from './pages/BusinessStrategySoftwarePage';
import StrategicAlignmentSoftwarePage from './pages/StrategicAlignmentSoftwarePage';
import OkrSoftwarePage from './pages/OkrSoftwarePage';
import StrategyManagementSoftwarePage from './pages/StrategyManagementSoftwarePage';
import StrategicPlanningToolsPage from './pages/StrategicPlanningToolsPage';
import BusinessPlanningSoftwarePage from './pages/BusinessPlanningSoftwarePage';
import StrategicExecutionPlatformPage from './pages/StrategicExecutionPlatformPage';
import CorporateStrategySoftwarePage from './pages/CorporateStrategySoftwarePage';
import DashboardPage from './pages/DashboardPage';
import CompanyWorkspaceLayout from './components/CompanyWorkspaceLayout';
import ExecutionHubPage from './pages/ExecutionHubPage';
import GBCTransitionPage from './pages/GBCTransitionPage';

import CompanySetupPage from './pages/CompanySetupPage';
import CompanyContextPage from './pages/CompanyContextPage';
import AIPlanPage from './ai-plans/PlanWorkspace';
import CompanyDashboardPage from './pages/CompanyDashboardPage';
import CompanyStrategyPage from './pages/CompanyStrategyPage';
import CompanyAdminPage from './pages/CompanyAdminPage';
import OrgChartPage from './pages/OrgChartPage';
import OrganizationalPage from './pages/OrganizationalPage';
import StrategyPage from './pages/StrategyPage';
import StrategyStepRouter from './pages/strategy/StrategyStepRouter';
import VisualReportPage from './pages/VisualReportPage';
import InvitationPage from './pages/InvitationPage';
import NotFoundPage from './pages/NotFoundPage';
import ApplicationAdminPage from './pages/ApplicationAdminPage';
import BlogAdminPage from './pages/BlogAdminPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import AboutPage from './pages/AboutPage';
import CareersPage from './pages/CareersPage';
import ContactPage from './pages/ContactPage';
import HealthCheckPage from './pages/HealthCheckPage';
import HealthCheckCompletePage from './pages/HealthCheckCompletePage';
import HealthCheckVerifyPage from './pages/HealthCheckVerifyPage';
import AcceptSignInPage from './pages/AcceptSignInPage';
import PrivacyPage from './pages/PrivacyPage';
import PricingPage from './pages/PricingPage';
import BillingPage from './pages/BillingPage';
import StrategicFrameworkPage from './pages/StrategicFrameworkPage';
import AIInsightsPage from './pages/AIInsightsPage';
import TeamCollaborationPage from './pages/TeamCollaborationPage';
import PerformanceTrackingPage from './pages/PerformanceTrackingPage';
import FoundationPage from './pages/FoundationPage';
import ExternalAssessmentPage from './pages/ExternalAssessmentPage';
import InternalAssessmentPage from './pages/InternalAssessmentPage';
import StrategyFormulationPage from './pages/StrategyFormulationPage';
import ExecutionMonitoringPage from './pages/ExecutionMonitoringPage';
import PublicLayout from './components/PublicLayout';

// Foundation sub-pages
import VisionStatementPage from './pages/foundation/VisionStatementPage';
import MissionStatementPage from './pages/foundation/MissionStatementPage';
import CoreValuesPage from './pages/foundation/CoreValuesPage';
import StrategicObjectivesPage from './pages/foundation/StrategicObjectivesPage';

// External Assessment sub-pages
import PortersFiveForcesPage from './pages/external-assessment/PortersFiveForcesPage';
import PestelAnalysisPage from './pages/external-assessment/PestelAnalysisPage';
import MarketAnalysisPage from './pages/external-assessment/MarketAnalysisPage';
import CompetitivePositioningPage from './pages/external-assessment/CompetitivePositioningPage';

// Internal Assessment sub-pages
import SwotAnalysisPage from './pages/internal-assessment/SwotAnalysisPage';
import CoreCompetenciesPage from './pages/internal-assessment/CoreCompetenciesPage';
import ResourceAuditPage from './pages/internal-assessment/ResourceAuditPage';

// Strategy Formulation sub-pages
import CompetitiveStrategyPage from './pages/strategy-formulation/CompetitiveStrategyPage';
import GrowthStrategyPage from './pages/strategy-formulation/GrowthStrategyPage';
import StrategicInitiativesPage from './pages/strategy-formulation/StrategicInitiativesPage';

// Execution & Monitoring sub-pages
import OkrsKpisPage from './pages/execution-monitoring/OkrsKpisPage';
import StrategicCadencePage from './pages/execution-monitoring/StrategicCadencePage';
import ContinuousImprovementPage from './pages/execution-monitoring/ContinuousImprovementPage';
import AccountabilityOwnershipPage from './pages/execution-monitoring/AccountabilityOwnershipPage';
import { CLOUDFLARE_STATIC_ASSETS } from './config/cloudflareAssets';
import FounderGuard from './components/FounderGuard';
import AdviserDirectoryPage from './pages/advisers/AdviserDirectoryPage';
import AdviserProfilePage from './pages/advisers/AdviserProfilePage';
import AdviserWorkspacePage from './pages/advisers/AdviserWorkspacePage';
import AdviserInvitationPage from './pages/advisers/AdviserInvitationPage';
import { adviserAuthReturn, protectedSignInPath } from './advisers/invitations';

// Import Clerk publishable key
const PUBLISHABLE_KEY = (import.meta as any).env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Publishable Key');
}

// Stable auth route wrappers so Clerk SignIn/SignUp are not remounted when the URL
// changes (e.g. /sign-up -> /sign-up/verify-email-address), which would trigger
// a second verification email. Use components instead of inline JSX so the same
// instance is preserved across parent re-renders.
const sharedAuthAppearance = {
  variables: {
    colorPrimary: '#205bff',
    colorBackground: '#ffffff',
    colorText: '#101938',
    colorTextSecondary: '#596789',
    fontFamily: 'StratPlan Inter, Arial, sans-serif',
    borderRadius: '0.75rem',
  },
  elements: {
    rootBox: 'mx-auto',
    card: 'border border-slate-200 rounded-xl',
    headerTitle: 'text-2xl font-bold text-gray-900',
    headerSubtitle: 'text-gray-600',
    socialButtonsIconButton: 'border border-gray-300 hover:border-gray-400',
    formButtonPrimary: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90',
    footerActionLink: 'text-blue-600 hover:text-blue-700',
  },
};

function SignInRoute() {
  const adviserReturn = adviserAuthReturn(useLocation().search);
  return (
    <PublicLayout mode="sign-in">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SignIn
          routing="path"
          path="/sign-in"
          fallbackRedirectUrl="/dashboard"
          forceRedirectUrl={adviserReturn}
          signUpForceRedirectUrl={adviserReturn}
          signUpUrl={
            adviserReturn
              ? `/sign-up?redirect_url=${encodeURIComponent(adviserReturn)}`
              : '/sign-up'
          }
          appearance={sharedAuthAppearance}
        />
      </motion.div>
    </PublicLayout>
  );
}

function SignUpRoute() {
  const adviserReturn = adviserAuthReturn(useLocation().search);
  return (
    <PublicLayout mode="sign-up">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SignUp
          routing="path"
          path="/sign-up"
          fallbackRedirectUrl="/dashboard"
          forceRedirectUrl={adviserReturn}
          signInForceRedirectUrl={adviserReturn}
          signInUrl={
            adviserReturn
              ? `/sign-in?redirect_url=${encodeURIComponent(adviserReturn)}`
              : '/sign-in'
          }
          appearance={sharedAuthAppearance}
        />
      </motion.div>
    </PublicLayout>
  );
}

// Protected Route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-strategy-50 to-blue-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <motion.img
            src={CLOUDFLARE_STATIC_ASSETS.LOGO_ICON}
            alt="StratPlan"
            className="h-16 w-16 mx-auto mb-4"
            animate={{
              scale: [1, 0.8, 1.2, 1],
              rotate: [0, 10, -10, 0],
            }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-gray-600 font-medium"
          >
            Loading...
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to={protectedSignInPath(location.pathname, location.search)} replace />;
  }

  return <>{children}</>;
}

// Main authenticated app component
function AuthenticatedApp() {
  const { isSupportSession } = useAppUser();
  const { isSignedIn } = useAuth();

  // Don't block public pages with loading - render routes immediately.
  // Protected routes handle their own loading via ProtectedRoute.
  // When !isLoaded, isSignedIn is undefined (falsy), so public routes render correctly.
  return (
    <InviteTokenCapture>
      <RouteAnalytics />
      <PageSEO />
      <ImpersonationBanner />
      <div className="min-h-screen">
        <Routes>
          {/* Public routes */}
          <Route
            path="/"
            element={isSignedIn ? <Navigate to="/dashboard" replace /> : <LandingPage />}
          />
          <Route
            path="/fb"
            element={isSignedIn ? <Navigate to="/dashboard" replace /> : <FBLandingPage />}
          />
          <Route
            path="/start"
            element={isSignedIn ? <Navigate to="/dashboard" replace /> : <FBLandingPage />}
          />
          <Route
            path="/strategic-planning-software"
            element={
              isSignedIn ? <Navigate to="/dashboard" replace /> : <StrategicPlanningSoftwarePage />
            }
          />
          <Route
            path="/strategy-execution-software"
            element={
              isSignedIn ? <Navigate to="/dashboard" replace /> : <StrategyExecutionSoftwarePage />
            }
          />
          <Route
            path="/business-strategy-software"
            element={
              isSignedIn ? <Navigate to="/dashboard" replace /> : <BusinessStrategySoftwarePage />
            }
          />
          <Route
            path="/strategic-alignment-software"
            element={
              isSignedIn ? <Navigate to="/dashboard" replace /> : <StrategicAlignmentSoftwarePage />
            }
          />
          <Route
            path="/okr-software"
            element={isSignedIn ? <Navigate to="/dashboard" replace /> : <OkrSoftwarePage />}
          />
          <Route
            path="/strategy-management-software"
            element={
              isSignedIn ? <Navigate to="/dashboard" replace /> : <StrategyManagementSoftwarePage />
            }
          />
          <Route
            path="/strategic-planning-tools"
            element={
              isSignedIn ? <Navigate to="/dashboard" replace /> : <StrategicPlanningToolsPage />
            }
          />
          <Route
            path="/business-planning-software"
            element={
              isSignedIn ? <Navigate to="/dashboard" replace /> : <BusinessPlanningSoftwarePage />
            }
          />
          <Route
            path="/strategic-execution-platform"
            element={
              isSignedIn ? <Navigate to="/dashboard" replace /> : <StrategicExecutionPlatformPage />
            }
          />
          <Route
            path="/corporate-strategy-software"
            element={
              isSignedIn ? <Navigate to="/dashboard" replace /> : <CorporateStrategySoftwarePage />
            }
          />

          {/* Auth routes - use stable components so Clerk is not remounted on path change (avoids double verification email) */}
          <Route path="/sign-in/*" element={<SignInRoute />} />
          <Route path="/sign-up/*" element={<SignUpRoute />} />

          {/* Invitation route */}
          <Route
            path="/invitation"
            element={isSupportSession ? <Navigate to="/dashboard" replace /> : <InvitationPage />}
          />

          <Route path="/advisers" element={<AdviserDirectoryPage />} />
          <Route path="/advisers/:slug" element={<AdviserProfilePage />} />
          <Route
            path="/adviser"
            element={
              <ProtectedRoute>
                <AdviserWorkspacePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/adviser/invitations/accept"
            element={
              <ProtectedRoute>
                <AdviserInvitationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/adviser/client-invitations/accept"
            element={
              <ProtectedRoute>
                <AdviserInvitationPage clientOwnership />
              </ProtectedRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <ApplicationAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/setup"
            element={
              <ProtectedRoute>
                <CompanySetupPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/:companyId"
            element={
              <ProtectedRoute>
                <FounderGuard>
                  <CompanyWorkspaceLayout />
                </FounderGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<CompanyDashboardPage />} />
            <Route path="context" element={<CompanyContextPage />} />
            <Route path="ai-plan" element={<AIPlanPage />} />
            <Route path="strategy" element={<CompanyStrategyPage />} />
            <Route path="strategy/:step" element={<StrategyStepRouter />} />
            <Route path="strategy/:step/:subPage" element={<StrategyStepRouter />} />
            <Route path="admin" element={<CompanyAdminPage />} />
            <Route path="org-chart" element={<OrgChartPage />} />
            <Route path="organization" element={<OrganizationalPage />} />
            <Route path="execution" element={<ExecutionHubPage />} />
            <Route path="visual-report" element={<VisualReportPage />} />
            <Route path="strategy/:strategyId" element={<StrategyPage />} />
          </Route>

          <Route
            path="/user-profile/*"
            element={
              <ProtectedRoute>
                <PublicLayout>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <>
                      {isSupportSession ? (
                        <p className="adviser-notice">
                          Account settings are unavailable during support. Exit support to manage
                          your administrator account.
                        </p>
                      ) : (
                        <UserProfile routing="path" path="/user-profile" />
                      )}
                    </>
                  </motion.div>
                </PublicLayout>
              </ProtectedRoute>
            }
          />

          {/* Blog routes */}
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Health Check */}
          <Route path="/health-check" element={<HealthCheckPage />} />
          <Route path="/health-check/complete" element={<HealthCheckCompletePage />} />
          <Route
            path="/health-check/verify"
            element={
              isSupportSession ? <Navigate to="/dashboard" replace /> : <HealthCheckVerifyPage />
            }
          />
          {/* One-click sign-in from email link (ticket from backend) */}
          <Route
            path="/accept-sign-in"
            element={isSupportSession ? <Navigate to="/dashboard" replace /> : <AcceptSignInPage />}
          />

          {/* Product feature pages */}
          <Route path="/strategic-framework" element={<StrategicFrameworkPage />} />
          <Route path="/ai-insights" element={<AIInsightsPage />} />
          <Route path="/team-collaboration" element={<TeamCollaborationPage />} />
          <Route path="/performance-tracking" element={<PerformanceTrackingPage />} />

          {/* Strategy step pages */}
          <Route path="/foundation" element={<FoundationPage />} />
          <Route path="/external-assessment" element={<ExternalAssessmentPage />} />
          <Route path="/internal-assessment" element={<InternalAssessmentPage />} />
          <Route path="/strategy-formulation" element={<StrategyFormulationPage />} />
          <Route path="/execution-monitoring" element={<ExecutionMonitoringPage />} />

          {/* Foundation sub-pages */}
          <Route path="/foundation/vision-statement" element={<VisionStatementPage />} />
          <Route path="/foundation/mission-statement" element={<MissionStatementPage />} />
          <Route path="/foundation/core-values" element={<CoreValuesPage />} />
          <Route path="/foundation/strategic-objectives" element={<StrategicObjectivesPage />} />

          {/* External Assessment sub-pages */}
          <Route
            path="/external-assessment/porters-five-forces"
            element={<PortersFiveForcesPage />}
          />
          <Route path="/external-assessment/pestel-analysis" element={<PestelAnalysisPage />} />
          <Route path="/external-assessment/market-analysis" element={<MarketAnalysisPage />} />
          <Route
            path="/external-assessment/competitive-positioning"
            element={<CompetitivePositioningPage />}
          />

          {/* Internal Assessment sub-pages */}
          <Route path="/internal-assessment/swot-analysis" element={<SwotAnalysisPage />} />
          <Route path="/internal-assessment/core-competencies" element={<CoreCompetenciesPage />} />
          <Route path="/internal-assessment/resource-audit" element={<ResourceAuditPage />} />

          {/* Strategy Formulation sub-pages */}
          <Route
            path="/strategy-formulation/competitive-strategy"
            element={<CompetitiveStrategyPage />}
          />
          <Route path="/strategy-formulation/growth-strategy" element={<GrowthStrategyPage />} />
          <Route
            path="/strategy-formulation/strategic-initiatives"
            element={<StrategicInitiativesPage />}
          />

          {/* Execution & Monitoring sub-pages */}
          <Route path="/execution-monitoring/okrs-kpis" element={<OkrsKpisPage />} />
          <Route
            path="/execution-monitoring/strategic-cadence"
            element={<StrategicCadencePage />}
          />
          <Route
            path="/execution-monitoring/continuous-improvement"
            element={<ContinuousImprovementPage />}
          />
          <Route
            path="/execution-monitoring/accountability-ownership"
            element={<AccountabilityOwnershipPage />}
          />

          {/* Company info pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/pricing" element={<PricingPage />} />

          <Route
            path="/admin/blog"
            element={
              <ProtectedRoute>
                <BlogAdminPage />
              </ProtectedRoute>
            }
          />

          {/* Catch all route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </InviteTokenCapture>
  );
}

function App() {
  const location = useLocation();

  // Check if accessed from GBC domain and redirect to transition page
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const isGBCDomain =
        hostname === 'globalbusinessconsultants.com.au' ||
        hostname === 'www.globalbusinessconsultants.com.au';

      // If accessed from GBC domain and not already on transition page, redirect
      if (
        isGBCDomain &&
        location.pathname !== '/gbc-transition' &&
        location.pathname !== '/transition'
      ) {
        window.location.href = '/gbc-transition';
        return;
      }
    }
  }, [location.pathname]);

  // Handle GBC transition routes completely outside of Clerk context
  if (location.pathname === '/gbc-transition' || location.pathname === '/transition') {
    return (
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        signInFallbackRedirectUrl="/dashboard"
        signUpFallbackRedirectUrl="/dashboard"
      >
        <AnalyticsProvider>
          <PageSEO />
          <GBCTransitionPage />
        </AnalyticsProvider>
      </ClerkProvider>
    );
  }

  // For all other routes, use the authenticated app with Clerk
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/dashboard"
    >
      <AnalyticsProvider>
        <SupportSessionProvider>
          <AuthenticatedApp />
        </SupportSessionProvider>
      </AnalyticsProvider>
    </ClerkProvider>
  );
}

export default App;
